/**
 * CashFlow Statement page.
 * Kiyosaki-inspired four-quadrant financial statement for a two-person household.
 * Primary data source: CSV upload. Falls back to demo data on first load.
 */

import { useState } from 'react';
import { useFinancialData } from '../hooks/useFinancialData';
import { useCSVParser } from '../hooks/useCSVParser';
import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/permissions';
import HeroBanner from '../components/cashflow/HeroBanner';
import IncomePanel from '../components/cashflow/IncomePanel';
import ExpensePanel from '../components/cashflow/ExpensePanel';
import AssetPanel from '../components/cashflow/AssetPanel';
import LiabilityPanel from '../components/cashflow/LiabilityPanel';
import TrendCharts from '../components/cashflow/TrendCharts';
import CSVUploader from '../components/cashflow/CSVUploader';
import QuickAddDrawer from '../components/cashflow/QuickAddDrawer';
import SyncButton from '../components/cashflow/SyncButton';
import DataAuditPanel from '../components/cashflow/DataAuditPanel';
import { formatMonth } from '../utils/cashflowCalc';

type View = 'statement' | 'history' | 'data' | 'upload';

export default function CashFlow() {
  const {
    summaries,
    assets,
    liabilities,
    quickAdds,
    allMetrics,
    latestMetrics,
    isDemo,
    loadCSVData,
    addQuickEntry,
    addAnnotation,
    getMonthAnnotation,
    getAssetsForMonth,
    getLiabilitiesForMonth,
    refetch,
  } = useFinancialData();
  const { user } = useAuth();

  const { parseFiles, parsing, parseErrors } = useCSVParser();

  const [view, setView] = useState<View>('statement');
  const [selectedMonth, setSelectedMonth] = useState(latestMetrics?.month ?? summaries[summaries.length - 1]?.month ?? '');

  const selectedSummary = summaries.find((s) => s.month === selectedMonth) ?? summaries[summaries.length - 1];
  const selectedMetrics = allMetrics.find((m) => m.month === selectedMonth) ?? latestMetrics;
  const selectedAssets = getAssetsForMonth(selectedMonth);
  const selectedLiabilities = getLiabilitiesForMonth(selectedMonth);
  const selectedAnnotation = getMonthAnnotation(selectedMonth);

  async function handleCSVUpload(files: File[]) {
    const result = await parseFiles(files);
    if (result.summaries.length > 0 || result.assets.length > 0 || result.liabilities.length > 0) {
      loadCSVData(
        result.summaries.length > 0 ? result.summaries : summaries,
        result.assets.length > 0 ? result.assets : assets,
        result.liabilities.length > 0 ? result.liabilities : liabilities,
      );
      setView('statement');
      const latestMonth = result.summaries.sort((a, b) => b.month.localeCompare(a.month))[0]?.month;
      if (latestMonth) setSelectedMonth(latestMonth);
    }
  }

  if (!selectedSummary || !selectedMetrics) {
    return <div className="page-container"><p className="loading-text">No data available.</p></div>;
  }

  return (
    <div className="page-container" style={{ maxWidth: 1200 }}>
      {/* Page header */}
      <header className="page-header" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--spacing-md)' }}>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>CashFlow Statement</h1>
            <p style={{ color: 'var(--color-text-secondary)', margin: '4px 0 0', fontSize: '0.9rem' }}>
              Kiyosaki financial statement · {formatMonth(selectedMonth)}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['statement', 'history', 'data', 'upload'] as View[]).map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: view === v ? 'var(--color-accent)' : '#fff',
                    color: view === v ? '#fff' : 'var(--color-text-primary)',
                    fontWeight: view === v ? 700 : 400,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    textTransform: 'capitalize',
                  }}
                >
                  {v === 'statement' ? '📊 Statement' :
                   v === 'history' ? '📈 History' :
                   v === 'data' ? '📋 Data' :
                   '📂 Upload CSV'}
                </button>
              ))}
            </div>
            <SyncButton
              onSyncComplete={refetch}
              isAdmin={Boolean(user && isAdmin(user.role))}
            />
          </div>
        </div>
      </header>

      {/* Hero banner — always visible */}
      <HeroBanner metrics={selectedMetrics} isDemo={isDemo} />

      {/* Month annotation */}
      {selectedAnnotation && (
        <div style={{ background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 'var(--spacing-lg)', fontSize: '0.9rem' }}>
          📝 <strong>{formatMonth(selectedMonth)}:</strong> {selectedAnnotation}
        </div>
      )}

      {/* STATEMENT VIEW */}
      {view === 'statement' && (
        <div>
          {/* Income Statement — top half */}
          <div style={{ marginBottom: 'var(--spacing-sm)' }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-sm)', fontWeight: 700 }}>
              — INCOME STATEMENT —
            </div>
            <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
              <IncomePanel summary={selectedSummary} assets={selectedAssets} />
              <ExpensePanel summary={selectedSummary} />
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '2px solid var(--color-border)', margin: 'var(--spacing-lg) 0', position: 'relative' }}>
            <span style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: 'var(--color-bg-light)', padding: '0 12px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--color-text-secondary)', fontWeight: 700 }}>
              BALANCE SHEET
            </span>
          </div>

          {/* Balance Sheet — bottom half */}
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
            <AssetPanel assets={selectedAssets} />
            <LiabilityPanel liabilities={selectedLiabilities} />
          </div>
        </div>
      )}

      {/* HISTORY VIEW */}
      {view === 'history' && (
        <TrendCharts
          allMetrics={allMetrics}
          summaries={summaries}
          selectedMonth={selectedMonth}
          onSelectMonth={(month) => { setSelectedMonth(month); setView('statement'); }}
        />
      )}

      {/* DATA AUDIT VIEW */}
      {view === 'data' && (
        <DataAuditPanel
          summaries={summaries}
          assets={assets}
          liabilities={liabilities}
          quickAdds={quickAdds}
        />
      )}

      {/* UPLOAD VIEW */}
      {view === 'upload' && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Upload CSV Data</h3>
          <CSVUploader onUpload={handleCSVUpload} parsing={parsing} errors={parseErrors} />
        </div>
      )}

      {/* Quick-add FAB */}
      <QuickAddDrawer
        currentMonth={selectedMonth}
        onAddEntry={addQuickEntry}
        onAddAnnotation={addAnnotation}
      />
    </div>
  );
}
